import React, { useRef, useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../middleware/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { getCountryByIso } from "../../../utils/country.utils";
import { useSearchParams } from "react-router-dom";
// Composants personnalisés
import PersonalInfoCard from "./Informations/PersonalInfoCard";
import AddressList from "./Informations/AddressList";
import AddressDrawer from "./Informations/AddressDrawer";
import DeleteAddressModal from "./Informations/DeleteAddressModal";
import MailSignature from "./Informations/MailSignature";

const UserInfo = () => {
    const { user, setUser } = useAuth();
    const drawerRef = useRef(null);
    const modalRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [queryParams] = useSearchParams();

  
    // Formik configuration
    const formik = useFormik({
        validateOnBlur: true,
        validateOnChange: false,
        initialValues: {
            street: "",
            city: "",
            postcode: "",
            isoCountry: "",
            state: "",
            isPrimary: false,
        },
        validationSchema: Yup.object({
            street: Yup.string().required("L'adresse est requise"),
            city: Yup.string().required("La ville est requise"),
            postcode: Yup.string().required("Le code postal est requis"),
            isoCountry: Yup.string().required("Le pays est requis"),
            state: Yup.string().optional(),
            isPrimary: Yup.boolean().required("Le statut principal est requis"),
        }),
        onSubmit: async (values) => {
            try {
                setIsLoading(true);
                if (isEditing) {
                    await updateAddress(selectedAddressId);
                } else {
                    const payload = {
                        ...values,
                        country: getCountryByIso(values.isoCountry).name
                    };
                    await createAddress(payload);
                }
                drawerRef.current.hideDrawer();
            } catch (error) {
                console.error("Erreur lors de la soumission du formulaire :", error);
                toast.error("Une erreur est survenue lors de la soumission du formulaire");
            } finally {
                setIsLoading(false);
            }
        }
    });

    // Gestion des adresses
    const deleteAddress = async (addressId) => {
        setIsLoading(true);
        try {
            const response = await api.delete(`/addresses/${addressId}`);
            if (response.status === 200) {
                const newAddresses = user.addresses.filter((address) => address.id !== addressId);
                setUser({ ...user, addresses: newAddresses });
                toast.success("Adresse supprimée avec succès");
            }
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la suppression de l'adresse");
        } finally {
            setIsLoading(false);
        }
    };

    const updateAddress = async (addressId) => {
        try {
            const payload = {
                ...formik.values,
                country: getCountryByIso(formik.values.isoCountry).name
            };

            const response = await api.put(`/addresses/${addressId}`, payload);

            if (response.status === 200) {
                const addressIndex = user.addresses.findIndex((address) => address.id === addressId);

                if (addressIndex !== -1) {
                    const userAddresses = [...user.addresses];
                    userAddresses[addressIndex] = response.data;

                    // Si la nouvelle adresse est définie comme principale, mettre à jour les autres
                    if (response.data.isPrimary) {
                        const currentPrimaryAddressIndex = user.addresses.findIndex(
                            (address) => address.isPrimary && address.id !== addressId
                        );

                        if (currentPrimaryAddressIndex !== -1) {
                            userAddresses[currentPrimaryAddressIndex].isPrimary = false;
                        }
                    }

                    setUser({ ...user, addresses: userAddresses });
                    toast.success(`Adresse modifiée avec succès ${response.data.isPrimary ? " (Adresse par défaut)" : ""}`);
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la mise à jour de l'adresse");
        }
    };

    const createAddress = async (address) => {
        try {
            const response = await api.post('/addresses/therapist', address);

            if (response.status === 200) {
                // Si la nouvelle adresse est principale, mettre à jour les autres adresses
                if (response.data.isPrimary) {
                    const updatedAddresses = user.addresses.map(addr => ({
                        ...addr,
                        isPrimary: false // Toutes les adresses ne sont plus principales
                    }));

                    setUser({
                        ...user,
                        addresses: [...updatedAddresses, response.data]
                    });
                } else {
                    setUser({
                        ...user,
                        addresses: [...user.addresses, response.data]
                    });
                }

                toast.success(`Adresse ajoutée avec succès ${response.data.isPrimary ? " (Adresse par défaut)" : ""}`);
            }
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la création de l'adresse");
        }
    };

    // Fonctions de gestion des actions
    const handleAddAddress = () => {
        setIsEditing(false);
        formik.resetForm();
        drawerRef.current.showDrawer();
    };

    const handleEditAddress = (addressId) => {
        const address = user.addresses.find((addr) => addr.id === addressId);

        if (address) {
            setIsEditing(true);
            setSelectedAddressId(address.id);

            // Remplir le formulaire avec les données de l'adresse
            formik.setValues({
                street: address.street,
                city: address.city,
                postcode: address.postcode,
                isoCountry: address.isoCountry,
                state: address.state,
                isPrimary: address.isPrimary
            });

            formik.errors = {};
            drawerRef.current.showDrawer();
        }
    };

    const handleDeleteAddress = (addressId) => {
        setSelectedAddressId(addressId);
        modalRef.current.showModal();
    };

    const handleConfirmDelete = () => {
        deleteAddress(selectedAddressId);
        modalRef.current.hideModal();
    };

    const handleCancelDelete = () => {
        modalRef.current.hideModal();
    };

    const handleHideDrawer = () => {
        drawerRef.current.hideDrawer();
    };
  // If query has createAddress, show drawer
    useEffect(() => {
        if (queryParams.get('createAddress')) {
            setIsEditing(false);
            formik.resetForm();
            drawerRef.current.showDrawer();
        }
    }, [queryParams]);

    const selectedAddress = user.addresses.find(address => address.id === selectedAddressId);

    return (
        <div className="space-y-4">
            {/* Grid layout pour écrans moyens et larges */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-min">
                {/* Informations personnelles */}
                <div className="h-auto">
                    <PersonalInfoCard user={user} />
                </div>

                {/* Liste des adresses */}
                <div className="h-auto">
                    <AddressList
                        addresses={user.addresses}
                        onAddAddress={handleAddAddress}
                        onEditAddress={handleEditAddress}
                        onDeleteAddress={handleDeleteAddress}
                    />
                </div>
            </div>

            {/* Signature email (pleine largeur) */}
            <MailSignature />

            {/* Formulaire d'adresse (tiroir) */}
            <AddressDrawer
                drawerRef={drawerRef}
                formik={formik}
                isEditing={isEditing}
                isLoading={isLoading}
                onHideDrawer={handleHideDrawer}
            />

            {/* Modal de confirmation de suppression */}
            <DeleteAddressModal
                modalRef={modalRef}
                selectedAddress={selectedAddress}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default UserInfo;
